import type { HotkeyBinding, HotkeyContext, HotkeyContextOptions } from './hotkeys.types'
import { isMac } from './isMac'

export class HotkeyService {
  private static instance: HotkeyService | null = null
  private contexts: Map<string, HotkeyContext> = new Map()
  private boundHandleKeyDown: (event: KeyboardEvent) => void
  private isInitialized = false

  private constructor() {
    this.boundHandleKeyDown = this.handleKeyDown.bind(this)
  }

  static getInstance(): HotkeyService {
    if (!HotkeyService.instance) {
      HotkeyService.instance = new HotkeyService()
    }
    return HotkeyService.instance
  }

  /** Reset the singleton instance - for testing only */
  static resetInstance(): void {
    HotkeyService.instance = null
  }

  registerContext(name: string, bindings: HotkeyBinding[], options?: HotkeyContextOptions): void {
    this.contexts.set(name, {
      name,
      bindings,
      isActive: false,
      priority: options?.priority ?? 0
    })
  }

  getContext(name: string): HotkeyContext | undefined {
    return this.contexts.get(name)
  }

  activateContext(name: string): void {
    const context = this.contexts.get(name)
    if (context) {
      context.isActive = true
    }
  }

  deactivateContext(name: string): void {
    const context = this.contexts.get(name)
    if (context) {
      context.isActive = false
    }
  }

  removeContext(name: string): void {
    this.contexts.delete(name)
  }

  init(): void {
    if (this.isInitialized) {
      return
    }
    document.addEventListener('keydown', this.boundHandleKeyDown)
    this.isInitialized = true
  }

  destroy(): void {
    document.removeEventListener('keydown', this.boundHandleKeyDown)
    this.isInitialized = false
  }

  handleKeyDown(event: KeyboardEvent): void {
    const isInInput = this.isInputElementFocused(event)

    const sortedContexts = Array.from(this.contexts.values()).sort((a, b) => b.priority - a.priority)

    for (const context of sortedContexts) {
      if (!context.isActive) {
        continue
      }

      for (const binding of context.bindings) {
        if (isInInput && !binding.allowInInputs) {
          continue
        }

        if (this.matchesBinding(event, binding)) {
          event.preventDefault()
          binding.handler(event)
          return
        }
      }
    }
  }

  getActiveBindings(): Array<{ contextName: string; bindings: HotkeyBinding[] }> {
    return Array.from(this.contexts.values())
      .filter(ctx => ctx.isActive)
      .sort((a, b) => b.priority - a.priority)
      .map(ctx => ({
        contextName: ctx.name,
        bindings: ctx.bindings.filter(b => b.description)
      }))
      .filter(ctx => ctx.bindings.length > 0)
  }

  private matchesBinding(event: KeyboardEvent, binding: HotkeyBinding): boolean {
    if (event.key !== binding.key) {
      return false
    }

    const isMod = binding.modifiers?.mod ?? false
    if (isMod && event.metaKey !== isMac()) {
      return false
    }

    const expectedCtrl = isMod ? !isMac() : (binding.modifiers?.ctrl ?? false)
    const expectedShift = binding.modifiers?.shift ?? false
    const expectedAlt = binding.modifiers?.alt ?? false

    return event.ctrlKey === expectedCtrl && event.shiftKey === expectedShift && event.altKey === expectedAlt
  }

  private isInputElementFocused(event: KeyboardEvent): boolean {
    const target = event.target as Element | null
    if (!target || !target.tagName) {
      return false
    }

    const tagName = target.tagName.toLowerCase()
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
      return true
    }

    if (target.getAttribute?.('contenteditable') === 'true') {
      return true
    }

    return false
  }
}
